import React, { useEffect, useMemo, useState } from "react"

import { FormProvider, useForm } from "react-hook-form"

import { FeatureFlagsExistenceLogicalOperator } from "@common-shared/config/featureFlags.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import {
    COMMON_ONE_VERIFY_DEMO_CONFIG,
    OneVerifyDemoPolicyId,
} from "@common-shared/config/oneVerify/oneVerifyDemo.config"

import { PrimaryText } from "@front-shared/elements/primaryText.element"

import {
    CHECKER_CONFIG,
    CoverageCheckerRunningState,
} from "../../../config/checker.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { DemoHelper } from "../../../helpers/demo.helper"
import { FeatureFlagsHelper } from "../../../helpers/featureFlags.helper"
import { useDemoMode } from "../../../hooks/useDemoMode.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import {
    checkerSetManualCheckType,
    checkerSetModalityCode,
} from "../../../store/slices/checker.slice"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types"
import type { IResolvedDemo } from "../../../types/objects.types"
import { CheckTypeSelectorComponent } from "../checkTypeSelector.component"
import { CheckerDynamicFormComponent } from "./checkerDynamicForm.component"
import { CheckerResultsComponent } from "./checkerResults.component"
import { DemoPolicyPickerComponent } from "./demoPolicyPicker.component"
import { ModalityCodeInput } from "./inputs/modalityCode.input"

export const CoverageCheckerManualComponent: React.FunctionComponent<
    ICoverageCheckerManualComponentProps
> = props => {
    const formMethods = useForm<IDynamicCheckerFormInputData>()

    const checkerState = useAppSelector(checkerSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const dispatch = useAppDispatch()
    const { isDemo } = useDemoMode()

    const showDemoPolicyPicker = isDemo || GENERAL_CONFIG.isSandbox

    // Same feature-flag gating as CheckTypeSelectorComponent's availableTabs
    // (modality limits omitted — demo policies drive modality, not the reverse).
    const availableManualCheckTypes = useMemo<QuotaUsageType[]>(() => {
        if (!runtimeState.enabledFeatureFlags) {
            return []
        }

        return CHECKER_CONFIG.manualTypeTabs
            .filter(item => {
                return item?.featureFlag
                    ? FeatureFlagsHelper.isFeatureFlagEnabled(
                          runtimeState.enabledFeatureFlags!,
                          item.featureFlag,
                          item.featureFlagExistenceOperator ||
                              FeatureFlagsExistenceLogicalOperator.OR,
                      )
                    : true
            })
            .map(item => item.key)
    }, [runtimeState.enabledFeatureFlags])

    const demoPolicies = useMemo(() => {
        return COMMON_ONE_VERIFY_DEMO_CONFIG.policies.filter(policy =>
            availableManualCheckTypes.includes(policy.checkType),
        )
    }, [availableManualCheckTypes])

    const [selectedDemoPolicyId, setSelectedDemoPolicyId] = useState<
        OneVerifyDemoPolicyId | undefined
    >(undefined)

    // Keep selection within flag-gated fixtures; demo mode always has one selected.
    useEffect(() => {
        const isSelectedAvailable = demoPolicies.some(
            policy => policy.id === selectedDemoPolicyId,
        )

        if (isSelectedAvailable) {
            return
        }

        if (isDemo && demoPolicies[0]) {
            setSelectedDemoPolicyId(demoPolicies[0].id)

            return
        }

        if (selectedDemoPolicyId) {
            setSelectedDemoPolicyId(undefined)
            dispatch(checkerSetManualCheckType(undefined))
            dispatch(checkerSetModalityCode(undefined))
            props.resetForm()
        }
    }, [demoPolicies, isDemo, selectedDemoPolicyId, dispatch, props.resetForm])

    const selectedDemoPolicy = useMemo(() => {
        return demoPolicies.find(item => item.id === selectedDemoPolicyId)
    }, [demoPolicies, selectedDemoPolicyId])

    const isDemoPolicySelected = !!selectedDemoPolicy

    // Policy and its resolved form data are kept together: parts of the form are
    // only seeded on mount, so rendering a policy with another policy's data
    // (while its payer lookup is still in flight) leaves stale fields behind.
    const [resolvedDemo, setResolvedDemo] = useState<IResolvedDemo | undefined>(
        undefined,
    )

    useEffect(() => {
        if (!selectedDemoPolicy) {
            setResolvedDemo(undefined)

            return
        }

        let cancelled = false

        void DemoHelper.toFormInputData(selectedDemoPolicy).then(formData => {
            if (cancelled) {
                return
            }

            if (!formData) {
                setSelectedDemoPolicyId(undefined)
                setResolvedDemo(undefined)

                return
            }

            setResolvedDemo({ policy: selectedDemoPolicy, formData })
        })

        return () => {
            cancelled = true
        }
    }, [selectedDemoPolicy])

    useEffect(() => {
        if (!resolvedDemo) {
            return
        }

        formMethods.reset(resolvedDemo.formData)
        dispatch(checkerSetModalityCode(resolvedDemo.formData.modalityCode))
        dispatch(checkerSetManualCheckType(resolvedDemo.policy.checkType))
    }, [resolvedDemo, formMethods, dispatch])

    // True while a newly picked policy is being resolved and the form still
    // shows the previously resolved one.
    const isDemoFormResolving =
        isDemoPolicySelected && resolvedDemo?.policy.id !== selectedDemoPolicyId

    const onDemoPolicyCleared = () => {
        // checkerReset preserves check type + modality; clear those first so the
        // remounted form matches the blank initial state (no NPI / expanded fields).
        setSelectedDemoPolicyId(undefined)
        setResolvedDemo(undefined)
        dispatch(checkerSetManualCheckType(undefined))
        dispatch(checkerSetModalityCode(undefined))

        props.resetForm()
    }

    const isInputView = useMemo<boolean>(() => {
        return [CoverageCheckerRunningState.INPUT_MANUAL].includes(
            checkerState.runningState,
        )
    }, [checkerState.runningState])

    const title = useMemo<JSX.Element | null>(() => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.INPUT_MANUAL:
                if (isDemo) {
                    return (
                        <>
                            Select a demo policy to populate the form with
                            sample values, then run the check.
                        </>
                    )
                }

                if (showDemoPolicyPicker) {
                    return isDemoPolicySelected ? (
                        <>
                            Form is locked to the selected demo policy. Clear
                            the selection to enter your own values.
                        </>
                    ) : (
                        <>
                            Fill out the information below, choose a
                            preconfigured check type, or select a demo policy to
                            run a sample check.
                        </>
                    )
                }

                return (
                    <>
                        Fill out the information you have below, or choose one
                        of the preconfigured check types.
                    </>
                )

            default:
                return null
        }
    }, [
        checkerState.runningState,
        isDemo,
        showDemoPolicyPicker,
        isDemoPolicySelected,
    ])

    const getCheckerByRunningState = (): JSX.Element | null => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.INPUT_MANUAL:
                if (isDemoPolicySelected) {
                    // Keyed on the resolved data so every resolution mounts a
                    // fresh form seeded with that policy's values.
                    return resolvedDemo ? (
                        <CheckerDynamicFormComponent
                            key={resolvedDemo.formData.id}
                            resetForm={props.resetForm}
                            isReadOnly
                            prefilledFormData={resolvedDemo.formData}
                            forcedCheckType={resolvedDemo.policy.checkType}
                        />
                    ) : null
                }

                return (
                    <CheckerDynamicFormComponent
                        key="blank"
                        resetForm={props.resetForm}
                    />
                )

            case CoverageCheckerRunningState.RESULTS_MANUAL:
                return <CheckerResultsComponent />

            default:
                return null
        }
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <FormProvider {...formMethods}>
                {checkerState.runningState ===
                CoverageCheckerRunningState.INPUT_MANUAL ? (
                    <>
                        {isDemo && title ? (
                            <PrimaryText weight="medium" className="mt-[20px]">
                                {title}
                            </PrimaryText>
                        ) : null}

                        {showDemoPolicyPicker ? (
                            <div
                                data-produktly="checker-demo-policy"
                                className="
                                    mt-[24px] max-w-[540px] w-full
                                    rounded-[8px] px-[16px] py-[16px] md:px-[24px] md:py-[20px]
                                    bg-brand-lilacLight
                                    border border-solid border-brand-warmLight2
                                "
                            >
                                <DemoPolicyPickerComponent
                                    policies={demoPolicies}
                                    selectedPolicy={selectedDemoPolicy}
                                    allowClear={!isDemo}
                                    onPolicySelected={setSelectedDemoPolicyId}
                                    onPolicyCleared={onDemoPolicyCleared}
                                />
                            </div>
                        ) : null}

                        {isDemoPolicySelected ? (
                            <ModalityCodeInput
                                key={
                                    resolvedDemo?.formData.id ?? "demoModality"
                                }
                                className="mt-[48px] w-[287px]"
                                disabled
                                onChange={() => undefined}
                                defaultValue={
                                    resolvedDemo?.formData.modalityCode
                                }
                            />
                        ) : (
                            <ModalityCodeInput
                                key={checkerState.modalityCode ?? "modality"}
                                className="mt-[48px] w-[287px]"
                                onChange={value => {
                                    if (!value) {
                                        return
                                    }

                                    dispatch(checkerSetModalityCode(value))
                                }}
                                defaultValue={checkerState.modalityCode}
                            />
                        )}
                    </>
                ) : null}

                {!isDemo && title ? (
                    <PrimaryText weight="medium" className="mt-[20px]">
                        {title}
                    </PrimaryText>
                ) : null}

                <div className="mt-[20px] flex items-stretch">
                    {isInputView ? (
                        <CheckTypeSelectorComponent
                            className="w-[220px] rounded-l-lg"
                            disabled={isDemoPolicySelected}
                        />
                    ) : null}

                    <div
                        className={`
                            relative p-[16px] bg-brand-offWhite2
                            ${
                                !isInputView
                                    ? "flex-1"
                                    : "w-[540px] rounded-r-lg overflow-hidden"
                            }
                            ${
                                isDemoFormResolving
                                    ? "opacity-50 pointer-events-none"
                                    : ""
                            }
                        `}
                    >
                        {getCheckerByRunningState()}
                    </div>
                </div>
            </FormProvider>
        </div>
    )
}

interface ICoverageCheckerManualComponentProps {
    className?: string
    resetForm: () => void
}
